import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Check } from "@phosphor-icons/react";

/**
 * Edit Scorecard modal — lets the player correct hole scores within the
 * 2-hour edit window. Submitting calls PUT /api/rounds/{id} and the parent
 * re-fetches the round.
 */
export function EditScorecardModal({ open, onClose, round, onSaved }) {
  const initial = useMemo(() =>
    (round?.holes || []).map((h) => ({
      number: h.number,
      par: h.par,
      strokes: h.strokes,
      putts: h.putts ?? null,
      fairway_hit: h.fairway_hit ?? null,
      gir: h.gir ?? null,
    })), [round]);

  const [holes, setHoles] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setHoles(initial); }, [initial]);

  if (!open) return null;

  const setStrokes = (idx, val) => {
    const n = Math.max(1, Math.min(15, Number(val) || 1));
    setHoles((curr) => curr.map((h, i) => (i === idx ? { ...h, strokes: n } : h)));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/rounds/${round.id}`, { holes });
      toast.success("Scorecard updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const total = holes.reduce((a, h) => a + (h.strokes || 0), 0);
  const par = holes.reduce((a, h) => a + (h.par || 0), 0);

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-4" data-testid="edit-scorecard-modal">
      <div className="w-full sm:max-w-2xl card-default p-5 sm:rounded-2xl rounded-t-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between gap-3">
          <div>
            <span className="label-eyebrow">Edit scorecard</span>
            <h3 className="font-heading font-bold text-xl mt-1">{round.course_name}</h3>
          </div>
          <button onClick={onClose} data-testid="edit-modal-close" className="text-[#0F2537]/60 hover:text-[#0F2537] p-1"><X size={20} /></button>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 overflow-y-auto flex-1">
          {holes.map((h, i) => (
            <div key={h.number} className="rounded-xl border border-[#E2E0D8] p-3" data-testid={`edit-hole-${h.number}`}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-bold">Hole {h.number}</span>
                <span className="text-[#0F2537]/60">Par {h.par}</span>
              </div>
              <input
                type="number" min={1} max={15} value={h.strokes} onChange={(e) => setStrokes(i, e.target.value)}
                data-testid={`edit-strokes-${h.number}`}
                className="mt-2 w-full px-3 py-2 text-center text-lg font-black rounded-lg border border-[#E2E0D8] bg-white focus:outline-none focus:border-[#0F2537]"
              />
            </div>
          ))}
        </div>

        <div className="mt-5 pt-4 border-t border-[#E2E0D8] flex items-center justify-between gap-3 shrink-0">
          <div className="text-sm">
            <span className="label-eyebrow">Total</span>
            <div className="font-heading font-black text-2xl">{total} <span className="text-sm text-[#0F2537]/60 font-normal">({total - par >= 0 ? "+" : ""}{total - par})</span></div>
          </div>
          <button onClick={save} disabled={saving} data-testid="edit-save-btn"
            className="px-5 py-3 rounded-xl bg-[#E05D3A] text-white font-bold inline-flex items-center gap-1.5 hover:brightness-110 disabled:opacity-60">
            <Check size={16} weight="bold" /> {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </div>
  );
}
